/* ===== SaaS Platform Super Admin Page ===== */

import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { PLATFORM_HOSTING_DOMAIN, PLATFORM_PRIMARY_DOMAIN, isPlatformReservedDomain, normalizeHostname, type PlatformMetrics, type PlatformOrganizationSummary, type SavePlatformOrganizationInput } from '@zetyar/data-contracts'
import { ROLES, ROLE_LABELS, PERMISSIONS, ROLE_PERMISSIONS, type RoleName } from '@zetyar/domain'
import { Building2, Check, CheckCircle, Database, Globe, Inbox, Pencil, Plus, RefreshCw, Search, ShieldCheck, Trash2, UserPlus, Users, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { useApp } from '@/presentation/providers/useApp'
import { createWebIdentityContext } from '@/infrastructure/repositories/organizations'
import { webPlatformRepository } from '@/infrastructure/repositories/platform'
import { webInvitationRepository } from '@/infrastructure/repositories/invitations'
import { uploadImageAsset } from '@/infrastructure/storage/images'
import { SaasLeadsCard } from './SaasLeadsCard'

const ROLE_ORDER: RoleName[] = [
  ROLES.ORG_ADMIN,
  ROLES.MANAGER,
  ROLES.TREASURER,
  ROLES.FINANCIAL_REVIEWER,
  ROLES.MODERATOR,
  ROLES.MEMBER,
  ROLES.VIEWER,
]

const PERMISSION_MODULE_LABELS: Record<string, string> = {
  organization: 'Organizacion',
  members: 'Miembros',
  announcements: 'Comunicados',
  projects: 'Proyectos',
  events: 'Actividades',
  documents: 'Documentos',
  channels: 'Chat',
  campaigns: 'Campañas',
  payments: 'Pagos',
  reports: 'Reportes',
  audit: 'Auditoria',
  finance: 'Finanzas',
  dues: 'Cuotas',
  supplies: 'Ventas y convenios',
}

const PERMISSIONS_BY_MODULE: { moduleKey: string; moduleLabel: string; permissions: string[] }[] = (() => {
  const groups = new Map<string, string[]>()
  for (const permission of Object.values(PERMISSIONS)) {
    const moduleKey = permission.split('.')[0] ?? permission
    groups.set(moduleKey, [...(groups.get(moduleKey) ?? []), permission])
  }
  return Array.from(groups.entries()).map(([moduleKey, permissions]) => ({
    moduleKey,
    moduleLabel: PERMISSION_MODULE_LABELS[moduleKey] ?? moduleKey,
    permissions,
  }))
})()

const emptyOrganizationForm: SavePlatformOrganizationInput = {
  name: '',
  slug: '',
  legalName: '',
  status: 'active',
  timezone: 'America/Santiago',
  locale: 'es-CL',
}

function slugify(value: string): string {
  return value.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function organizationMatchesInput(organization: PlatformOrganizationSummary, input: SavePlatformOrganizationInput): boolean {
  return organization.name === input.name &&
    organization.slug === input.slug &&
    organization.legalName === (input.legalName ?? null) &&
    organization.logoUrl === (input.logoUrl ?? null) &&
    organization.logoDarkUrl === (input.logoDarkUrl ?? null) &&
    organization.faviconUrl === (input.faviconUrl ?? null) &&
    organization.primaryDomain === (input.primaryDomain ?? null) &&
    organization.status === input.status &&
    organization.timezone === input.timezone &&
    organization.locale === input.locale
}

export function AdminSaasPage() {
  const { profile, user } = useApp()
  const [organizations, setOrganizations] = useState<PlatformOrganizationSummary[]>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [organizationForm, setOrganizationForm] = useState<SavePlatformOrganizationInput>(emptyOrganizationForm)
  const [editingOrganization, setEditingOrganization] = useState<PlatformOrganizationSummary | null>(null)
  const [isOrganizationEditorOpen, setIsOrganizationEditorOpen] = useState(false)
  const [isSavingOrganization, setIsSavingOrganization] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [isSavingDomain, setIsSavingDomain] = useState(false)
  const [adminEmail, setAdminEmail] = useState('')
  const [isInvitingAdmin, setIsInvitingAdmin] = useState(false)
  const [uploadingAsset, setUploadingAsset] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newLeadCount, setNewLeadCount] = useState<number | null>(null)

  // Stable identity for SaasLeadsCard's load effect (a new object per render would refetch forever).
  const identityContext = useMemo(
    () => (user && profile ? createWebIdentityContext({ authUid: user.uid, personId: profile.id }) : null),
    [profile, user],
  )

  const load = useCallback(async () => {
    if (!user || !profile) {
      setIsLoading(false)
      return
    }

    const context = createWebIdentityContext({ authUid: user.uid, personId: profile.id })

    try {
      setIsLoading(true)
      setError(null)
      const [orgs, platformMetrics] = await Promise.all([
        webPlatformRepository.listAllOrganizations(context, 500),
        webPlatformRepository.getMetrics(context),
      ])
      setOrganizations(orgs)
      setMetrics(platformMetrics)
      setSelectedOrganizationId((current) => current && orgs.some((org) => org.id === current) ? current : orgs[0]?.id ?? null)
    } catch (caught) {
      console.error('Error al cargar el panel SaaS:', caught)
      setError('No se pudo cargar el panel. Esta cuenta no tiene permisos de super administrador.')
      setOrganizations([])
      setSelectedOrganizationId(null)
      setMetrics(null)
    } finally {
      setIsLoading(false)
    }
  }, [profile, user])

  useEffect(() => {
    void load()
  }, [load])

  const filteredOrganizations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return organizations
    return organizations.filter((org) =>
      org.name.toLowerCase().includes(term) ||
      org.slug.toLowerCase().includes(term) ||
      org.status.toLowerCase().includes(term),
    )
  }, [organizations, searchTerm])

  const selectedOrganization = useMemo(
    () => organizations.find((org) => org.id === selectedOrganizationId) ?? filteredOrganizations[0] ?? null,
    [filteredOrganizations, organizations, selectedOrganizationId],
  )

  const activeOrganizations = organizations.filter((org) => org.status === 'active').length
  const suspendedOrganizations = organizations.length - activeOrganizations

  const openNewOrganization = () => {
    setEditingOrganization(null)
    setOrganizationForm(emptyOrganizationForm)
    setIsOrganizationEditorOpen(true)
  }

  const openEditOrganization = (organization: PlatformOrganizationSummary) => {
    setEditingOrganization(organization)
    setOrganizationForm({
      name: organization.name,
      slug: organization.slug,
      legalName: organization.legalName ?? '',
      logoUrl: organization.logoUrl,
      logoDarkUrl: organization.logoDarkUrl,
      faviconUrl: organization.faviconUrl,
      brandColor: null,
      primaryDomain: organization.primaryDomain,
      status: organization.status,
      timezone: organization.timezone,
      locale: organization.locale,
    })
    setIsOrganizationEditorOpen(true)
  }

  const closeOrganizationEditor = () => {
    setIsOrganizationEditorOpen(false)
    setEditingOrganization(null)
    setOrganizationForm(emptyOrganizationForm)
  }

  const platformContext = () => {
    if (!user || !profile) return null
    return createWebIdentityContext({ authUid: user.uid, personId: profile.id })
  }

  const uploadOrganizationLogo = async (file: File | undefined) => {
    if (!file || !editingOrganization) return
    try {
      setUploadingAsset('logo')
      const uploadedUrl = await uploadImageAsset({
        organizationId: editingOrganization.id,
        scope: 'branding',
        ownerId: 'logo',
        file,
        label: 'logo',
        maxBytes: 3 * 1024 * 1024,
      })
      const nextForm = {
        ...organizationForm,
        logoUrl: uploadedUrl,
        logoDarkUrl: uploadedUrl,
        faviconUrl: uploadedUrl,
      }
      setOrganizationForm(nextForm)

      const context = platformContext()
      if (context && webPlatformRepository.updateOrganization) {
        const input: SavePlatformOrganizationInput = {
          ...nextForm,
          name: nextForm.name.trim(),
          slug: slugify(nextForm.slug || nextForm.name),
          legalName: nextForm.legalName?.trim() || null,
          brandColor: null,
          timezone: nextForm.timezone.trim() || 'America/Santiago',
          locale: nextForm.locale.trim() || 'es-CL',
        }
        await webPlatformRepository.updateOrganization(context, editingOrganization.id, input)

        const persistedOrganizations = await webPlatformRepository.listAllOrganizations(context, 500)
        const persistedOrganization = persistedOrganizations.find((organization) => organization.id === editingOrganization.id)
        if (!persistedOrganization || !organizationMatchesInput(persistedOrganization, input)) {
          throw new Error('La imagen se subio, pero la URL no quedo persistida en Supabase.')
        }
        setOrganizations(persistedOrganizations)
        window.dispatchEvent(new Event('zetyar:organizations-changed'))
        await load()
      }
    } catch (caught) {
      console.error('Error al subir logo de organizacion:', caught)
      alert(caught instanceof Error ? caught.message : 'No se pudo subir el logo.')
    } finally {
      setUploadingAsset(null)
    }
  }

  const addDomain = async () => {
    const context = platformContext()
    const normalizedDomain = normalizeHostname(newDomain)
    if (!context || !selectedOrganization || !normalizedDomain || !webPlatformRepository.createDomain) return
    if (isPlatformReservedDomain(normalizedDomain)) {
      alert('Ese dominio pertenece a la plataforma Zetyar Nexo. Usa un dominio propio de la empresa cliente.')
      return
    }
    try {
      setIsSavingDomain(true)
      await webPlatformRepository.createDomain(context, selectedOrganization.id, {
        domain: normalizedDomain,
        isPrimary: selectedOrganization.domains.length === 0,
      })
      setNewDomain('')
      await load()
    } catch (caught) {
      console.error('Error al agregar dominio:', caught)
      alert('No se pudo agregar el dominio. Revisa que no exista en otra empresa.')
    } finally {
      setIsSavingDomain(false)
    }
  }

  const inviteAdmin = async () => {
    if (!user || !profile || !selectedOrganization) return
    const email = adminEmail.trim()
    if (!email) return

    try {
      setIsInvitingAdmin(true)
      await webInvitationRepository.create(
        { authUid: user.uid, personId: profile.id, organizationId: selectedOrganization.id },
        { email, role: 'organization_admin' },
      )
      setAdminEmail('')
      alert(`Invitacion enviada a ${email}. Debe aceptarla para quedar como administrador de ${selectedOrganization.name}.`)
    } catch (caught) {
      console.error('Error al invitar administrador:', caught)
      alert(caught instanceof Error ? caught.message : 'No se pudo invitar al administrador.')
    } finally {
      setIsInvitingAdmin(false)
    }
  }

  const verifyDomain = async (domainId: string, isPrimary: boolean) => {
    const context = platformContext()
    if (!context || !selectedOrganization || !webPlatformRepository.verifyDomain) return
    await webPlatformRepository.verifyDomain(context, selectedOrganization.id, domainId, isPrimary)
    await load()
  }

  const disableDomain = async (domainId: string) => {
    const context = platformContext()
    if (!context || !selectedOrganization || !webPlatformRepository.disableDomain) return
    if (!confirm('Desactivar este dominio?')) return
    await webPlatformRepository.disableDomain(context, selectedOrganization.id, domainId)
    await load()
  }
  const saveOrganization = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!user || !profile) return

    const input: SavePlatformOrganizationInput = {
      ...organizationForm,
      name: organizationForm.name.trim(),
      slug: slugify(organizationForm.slug || organizationForm.name),
      legalName: organizationForm.legalName?.trim() || null,
      logoUrl: organizationForm.logoUrl ?? null,
      logoDarkUrl: organizationForm.logoDarkUrl ?? organizationForm.logoUrl ?? null,
      faviconUrl: organizationForm.faviconUrl ?? organizationForm.logoUrl ?? null,
      brandColor: null,
      timezone: organizationForm.timezone.trim() || 'America/Santiago',
      locale: organizationForm.locale.trim() || 'es-CL',
    }
    if (!input.name || !input.slug) return

    const context = createWebIdentityContext({ authUid: user.uid, personId: profile.id })
    try {
      setIsSavingOrganization(true)
      if (editingOrganization) {
        if (!webPlatformRepository.updateOrganization) return
        await webPlatformRepository.updateOrganization(context, editingOrganization.id, input)

        const persistedOrganizations = await webPlatformRepository.listAllOrganizations(context, 500)
        const persistedOrganization = persistedOrganizations.find((organization) => organization.id === editingOrganization.id)
        if (!persistedOrganization || !organizationMatchesInput(persistedOrganization, input)) {
          throw new Error('La organizacion no quedo persistida en Supabase.')
        }
        setOrganizations(persistedOrganizations)
        window.dispatchEvent(new Event('zetyar:organizations-changed'))
      } else {
        if (!webPlatformRepository.createOrganization) return
        await webPlatformRepository.createOrganization(context, input)
        window.dispatchEvent(new Event('zetyar:organizations-changed'))
      }
      closeOrganizationEditor()
      await load()
    } catch (caught) {
      console.error('Error al guardar organizacion:', caught)
      alert(caught instanceof Error ? caught.message : 'No se pudo guardar la organizacion.')
    } finally {
      setIsSavingOrganization(false)
    }
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[24px] border border-neutral-200 bg-white p-6 shadow-card-elevated dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="h-7 w-7 text-primary-500" />
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Panel SaaS Zetyar</h1>
            </div>
            <p className="max-w-2xl text-sm text-neutral-500 dark:text-neutral-400">
              Gestion centralizada de organizaciones, recursos y auditoria global de Zetyar Nexo.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="secondary" leftIcon={<RefreshCw className="h-4 w-4" />} isLoading={isLoading} onClick={() => void load()}>
              Actualizar
            </Button>
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openNewOrganization}>
              Nueva organizacion
            </Button>
          </div>
        </div>
      </section>

      {error && (
        <Card className="border-danger-200 dark:border-red-900">
          <CardContent>
            <p className="text-sm text-danger-600 dark:text-red-300">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Dominio oficial de plataforma</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <DetailRow label="Dominio publico" value={PLATFORM_PRIMARY_DOMAIN} monospace />
            <DetailRow label="Hosting" value={PLATFORM_HOSTING_DOMAIN} monospace />
          </div>
          <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
            Estos dominios son reservados de Zetyar Nexo. No se asignan a empresas cliente; los dominios de clientes se agregan abajo por organizacion.
          </p>
        </CardContent>
      </Card>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <AdminMetric icon={<Building2 className="w-6 h-6" />} label="Organizaciones" value={isLoading ? '...' : String(metrics?.organizationCount ?? 0)} detail={`${activeOrganizations} activas`} tone="primary" />
        <AdminMetric icon={<Users className="w-6 h-6" />} label="Personas registradas" value={isLoading ? '...' : String(metrics?.personCount ?? 0)} detail="Supabase" tone="neutral" />
        <AdminMetric icon={<Database className="w-6 h-6" />} label="Base de datos" value="Postgres" detail="Supabase" tone="success" />
        <AdminMetric icon={<ShieldCheck className="w-6 h-6" />} label="Alertas" value={String(suspendedOrganizations)} detail="Organizaciones no activas" tone="warning" />
        <AdminMetric icon={<Inbox className="w-6 h-6" />} label="Leads nuevos" value={newLeadCount === null ? '...' : String(newLeadCount)} detail="Solicitudes de demo sin atender" tone="primary" />
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.6fr)]">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle>Organizaciones registradas</CardTitle>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Datos reales desde Supabase. Selecciona una fila para ver el detalle operativo.
                </p>
              </div>
              <div className="w-full md:w-72">
                <Input label="Buscar" value={searchTerm} leftIcon={<Search className="h-4 w-4" />} onChange={(event) => setSearchTerm(event.target.value)} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-neutral-50 dark:bg-neutral-950 text-neutral-500 dark:text-neutral-400 font-semibold border-b border-neutral-200 dark:border-neutral-800">
                  <tr>
                    <th className="p-3">Organizacion</th>
                    <th className="p-3">Slug</th>
                    <th className="p-3">Miembros activos</th>
                    <th className="p-3">Estado</th>
                    <th className="p-3">Alta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {isLoading ? (
                    <tr><td className="p-3 text-neutral-500 dark:text-neutral-400" colSpan={5}>Cargando...</td></tr>
                  ) : filteredOrganizations.length === 0 ? (
                    <tr><td className="p-3 text-neutral-500 dark:text-neutral-400" colSpan={5}>No hay organizaciones para mostrar.</td></tr>
                  ) : (
                    filteredOrganizations.map((org) => (
                      <tr key={org.id} className={`cursor-pointer hover:bg-neutral-50/80 dark:hover:bg-neutral-950 ${selectedOrganization?.id === org.id ? 'bg-primary-50/60 dark:bg-primary-950/30' : ''}`} onClick={() => setSelectedOrganizationId(org.id)}>
                        <td className="p-3 font-bold text-neutral-900 dark:text-neutral-100">
                          <div className="flex items-center gap-2">
                            {org.logoUrl ? <img src={org.logoUrl} alt={org.name} className="h-7 w-7 rounded-[10px] border border-neutral-200 object-contain p-0.5 dark:border-neutral-700" /> : <Building2 className="w-4 h-4 text-primary-600" />}
                            <span>{org.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-neutral-500 dark:text-neutral-400 font-mono text-xs">{org.slug}</td>
                        <td className="p-3 text-neutral-500 dark:text-neutral-400">{org.activeMemberCount}</td>
                        <td className="p-3"><OrganizationStatusBadge status={org.status} /></td>
                        <td className="p-3 text-neutral-500 dark:text-neutral-400 text-xs">{new Date(org.createdAt).toLocaleDateString('es-CL')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Detalle operativo</CardTitle></CardHeader>
          <CardContent>
            {!selectedOrganization ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Selecciona una organizacion.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Organizacion</p>
                  <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{selectedOrganization.name}</p>
                </div>
                <DetailRow label="ID" value={selectedOrganization.id} monospace />
                <DetailRow label="Slug" value={selectedOrganization.slug} monospace />
                <DetailRow label="Miembros activos" value={String(selectedOrganization.activeMemberCount)} />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Estado</span>
                  <OrganizationStatusBadge status={selectedOrganization.status} />
                </div>
                <DetailRow label="Zona horaria" value={selectedOrganization.timezone} />
                <DetailRow label="Idioma" value={selectedOrganization.locale} />
                <DetailRow label="Razon social" value={selectedOrganization.legalName || 'Sin razon social'} />
                <DetailRow label="Dominio principal" value={selectedOrganization.primaryDomain || 'Sin dominio principal'} />

                <DetailRow label="Fecha de alta" value={new Date(selectedOrganization.createdAt).toLocaleString('es-CL')} />
                <DetailRow label="Ultima actualizacion" value={new Date(selectedOrganization.updatedAt).toLocaleString('es-CL')} />
                <div className="space-y-3 rounded-[18px] border border-neutral-200 p-3 dark:border-neutral-800">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Dominios</span>
                    <Badge variant="neutral">{selectedOrganization.domains.length}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Input label="" placeholder="empresa.cl" value={newDomain} onChange={(event) => setNewDomain(event.target.value)} />
                    <Button type="button" variant="secondary" isLoading={isSavingDomain} onClick={() => void addDomain()} leftIcon={<Globe className="h-4 w-4" />}>
                      Agregar
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {selectedOrganization.domains.length === 0 ? (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Sin dominios registrados.</p>
                    ) : selectedOrganization.domains.map((domain) => (
                      <div key={domain.id} className="rounded-[13px] border border-neutral-200 p-3 text-xs dark:border-neutral-800">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-neutral-900 dark:text-neutral-100">{domain.domain}</span>
                          <OrganizationStatusBadge status={domain.status} />
                        </div>
                        <p className="mt-2 break-all text-neutral-500 dark:text-neutral-400">TXT: {domain.verificationToken}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {domain.status !== 'active' && (
                            <Button size="sm" variant="secondary" leftIcon={<Check className="h-3.5 w-3.5" />} onClick={() => void verifyDomain(domain.id, domain.isPrimary)}>
                              Marcar verificado
                            </Button>
                          )}
                          {!domain.isPrimary && domain.status === 'active' && (
                            <Button size="sm" variant="outline" onClick={() => void verifyDomain(domain.id, true)}>
                              Hacer principal
                            </Button>
                          )}
                          <Button size="sm" variant="danger" leftIcon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => void disableDomain(domain.id)}>
                            Desactivar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3 rounded-[18px] border border-neutral-200 p-3 dark:border-neutral-800">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Administrador</span>
                    <Badge variant="neutral">{selectedOrganization.activeMemberCount} miembro(s)</Badge>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Invita al primer administrador de esta organizacion. Debe aceptar la invitacion para quedar activo.
                  </p>
                  <div className="flex gap-2">
                    <Input label="" type="email" placeholder="admin@empresa.cl" value={adminEmail} onChange={(event) => setAdminEmail(event.target.value)} />
                    <Button type="button" variant="secondary" isLoading={isInvitingAdmin} onClick={() => void inviteAdmin()} leftIcon={<UserPlus className="h-4 w-4" />}>
                      Invitar
                    </Button>
                  </div>
                </div>
                <Button variant="outline" fullWidth leftIcon={<Pencil className="h-4 w-4" />} onClick={() => openEditOrganization(selectedOrganization)}>
                  Editar organizacion
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <SaasLeadsCard context={identityContext} onNewCountChange={setNewLeadCount} />

      <Card>
        <CardHeader>
          <CardTitle>Roles y permisos</CardTitle>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            Catalogo completo de permisos por rol, tal como se aplica en toda organizacion. Util para
            entender que vera cada rol y para pruebas con cuentas de distinto nivel.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className="sticky left-0 z-10 bg-white py-2 pr-4 text-left font-semibold text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100">
                    Permiso
                  </th>
                  {ROLE_ORDER.map((role) => (
                    <th key={role} className="px-3 py-2 text-center font-semibold text-neutral-700 dark:text-neutral-200 whitespace-nowrap">
                      {ROLE_LABELS[role]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSIONS_BY_MODULE.map((group) => (
                  <Fragment key={group.moduleKey}>
                    <tr className="bg-neutral-50 dark:bg-neutral-800/60">
                      <td colSpan={ROLE_ORDER.length + 1} className="sticky left-0 py-1.5 px-2 text-xs font-bold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                        {group.moduleLabel}
                      </td>
                    </tr>
                    {group.permissions.map((permission) => (
                      <tr key={permission} className="border-b border-neutral-100 dark:border-neutral-800/60">
                        <td className="sticky left-0 z-10 bg-white py-2 pr-4 font-mono text-xs text-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                          {permission}
                        </td>
                        {ROLE_ORDER.map((role) => {
                          const hasPermission = ROLE_PERMISSIONS[role].includes(permission)
                          return (
                            <td key={role} className="px-3 py-2 text-center">
                              {hasPermission ? (
                                <Check className="inline h-4 w-4 text-success-600 dark:text-success-400" />
                              ) : (
                                <X className="inline h-4 w-4 text-neutral-300 dark:text-neutral-700" />
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={isOrganizationEditorOpen}
        onClose={closeOrganizationEditor}
        title={editingOrganization ? 'Editar organizacion' : 'Nueva organizacion'}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={closeOrganizationEditor}>Cancelar</Button>
            <Button form="organization-form" type="submit" isLoading={isSavingOrganization}>Guardar</Button>
          </>
        }
      >
        <form id="organization-form" className="grid gap-4 sm:grid-cols-2" onSubmit={saveOrganization}>
          <Input label="Nombre" value={organizationForm.name} onChange={(event) => setOrganizationForm((current) => ({ ...current, name: event.target.value, slug: editingOrganization ? current.slug : slugify(event.target.value) }))} required />
          <Input label="Slug" value={organizationForm.slug} onChange={(event) => setOrganizationForm((current) => ({ ...current, slug: slugify(event.target.value) }))} required />
          <Input label="Razon social" className="sm:col-span-2" value={organizationForm.legalName ?? ''} onChange={(event) => setOrganizationForm((current) => ({ ...current, legalName: event.target.value }))} />
          <Input label="Zona horaria" value={organizationForm.timezone} onChange={(event) => setOrganizationForm((current) => ({ ...current, timezone: event.target.value }))} required />
          <Input label="Idioma" value={organizationForm.locale} onChange={(event) => setOrganizationForm((current) => ({ ...current, locale: event.target.value }))} required />
          <div className="sm:col-span-2 rounded-[18px] border border-neutral-200 p-4 dark:border-neutral-800">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Marca de la empresa</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Sube un logo real a Supabase Storage.</p>
              </div>
              {organizationForm.logoUrl && <img src={organizationForm.logoUrl} alt="Logo" className="h-12 w-12 rounded-[13px] border border-neutral-200 object-contain p-1 dark:border-neutral-700" />}
            </div>
            {!editingOrganization && <p className="mb-3 rounded-[13px] bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:bg-warning-500/15 dark:text-warning-100">Guarda la organizacion primero para habilitar la subida de archivos en su ruta Storage.</p>}
            <div className="grid gap-3">
              <label className="flex flex-col gap-1.5 text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                Logo principal
                <input type="file" accept="image/*" disabled={!editingOrganization || uploadingAsset != null} onChange={(event) => void uploadOrganizationLogo(event.target.files?.[0])} className="text-sm text-neutral-500" />
              </label>
            </div>
            {uploadingAsset && <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">Subiendo imagen...</p>}
          </div>
          <Input label="Dominio principal" placeholder="empresa.cl" value={organizationForm.primaryDomain ?? ''} onChange={(event) => setOrganizationForm((current) => ({ ...current, primaryDomain: event.target.value }))} />
          <label className="flex flex-col gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Estado
            <select className="h-11 rounded-[13px] border border-neutral-200 bg-white px-3.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100" value={organizationForm.status} onChange={(event) => setOrganizationForm((current) => ({ ...current, status: event.target.value }))}>
              <option value="active">Activa</option>
              <option value="suspended">Suspendida</option>
              <option value="inactive">Inactiva</option>
            </select>
          </label>
        </form>
      </Modal>
    </div>
  )
}

function OrganizationStatusBadge({ status }: { readonly status: string }) {
  return (
    <Badge variant={status === 'active' ? 'success' : 'neutral'} className="flex items-center gap-1 w-fit">
      <CheckCircle className="w-3 h-3" /> {status === 'active' ? 'Activa' : status}
    </Badge>
  )
}

function DetailRow({ label, value, monospace = false }: { readonly label: string; readonly value: string; readonly monospace?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-sm text-neutral-500 dark:text-neutral-400">{label}</span>
      <span className={`text-right text-sm text-neutral-900 dark:text-neutral-100 ${monospace ? 'font-mono break-all' : 'font-medium'}`}>{value}</span>
    </div>
  )
}

function AdminMetric({ icon, label, value, detail, tone }: { readonly icon: ReactNode; readonly label: string; readonly value: string; readonly detail: string; readonly tone: 'primary' | 'neutral' | 'success' | 'warning' }) {
  const toneClasses = {
    primary: 'bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-200',
    neutral: 'bg-neutral-100 dark:bg-neutral-800 text-primary-700 dark:text-primary-200',
    success: 'bg-success-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300',
    warning: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-300',
  }

  return (
    <Card className="flex items-center gap-4">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[13px] ${toneClasses[tone]}`}>{icon}</div>
      <div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">{label}</p>
        <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{value}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">{detail}</p>
      </div>
    </Card>
  )
}
